import React, { useEffect, useState } from "react";
import logo from '../assets/logo-mobile.svg';
import { FaArrowRight, FaBars, FaChevronDown } from "react-icons/fa";
import { useNavigate, useLocation } from "react-router-dom";
import { MENU_FEATURES, FEATURED_FEATURE, MENU_SOLUTIONS } from "../pages/solutions/solutions-data";
import { useProductFlow, PRODUCT_MANAGEMENT } from "../utils/product-flow";
import { IS_DUAL_MODE } from "../utils/site-mode";

const PRODUCTS = [
    { label: "Project Management", path: "/products/project-management" },
    { label: "Published", path: "/" },
];

const MobileNav = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { product } = useProductFlow();
    const isManagement = product === PRODUCT_MANAGEMENT;
    const [isDrawerOpen, setDrawerOpen] = useState(false);
    const [productsOpen, setProductsOpen] = useState(false);
    const [featuresOpen, setFeaturesOpen] = useState(false);
    const [solutionsOpen, setSolutionsOpen] = useState(false);

    useEffect(() => {
        if (location.state?.scrollTo) {
            const section = document.getElementById(location.state.scrollTo);
            if (section) section.scrollIntoView({ behavior: "smooth" });
        }
    }, [location]);

    const isActive = (path) => location.pathname === path;

    const SECTION_PATHS = {
        features: "/platform/features",
        pricing: "/pricing",
        about: "/about",
    };

    const handleNavigation = (section) => {
        const path = section.toLowerCase();
        setDrawerOpen(false);
        setProductsOpen(false);

        if (path === "pricing") { navigate("/pricing"); return; }
        if (path === "about") { navigate("/about"); return; }
        if (path === "contact") { navigate("/contact-us"); return; }
        if (path === "features") { navigate("/platform/features"); return; }

        if (location.pathname !== "/") {
            navigate("/", { state: { scrollTo: path } });
        } else {
            document.getElementById(path)?.scrollIntoView({ behavior: "smooth" });
        }
    };

    return (
        <nav className="mobile-nav">
            <div className="mobile-nav-header">
                <div className="mobile-nav-left">
                    <button className="hamburger-btn" onClick={() => setDrawerOpen(!isDrawerOpen)}>
                        <FaBars size={20} />
                    </button>
                    <button className="unoutlined-button" onClick={() => navigate("/")}>
                        <img src={logo} alt="ConRa" className="mobile-nav-icon-full" />
                    </button>
                </div>
                <button
                    className="get-started"
                    onClick={() => {
                        if (location.pathname === "/solutions/borderless") {
                            document.getElementById("register")?.scrollIntoView({ behavior: "smooth" });
                        } else {
                            window.open("https://web.conra.net", "_blank");
                        }
                    }}
                >
                    Get Started <FaArrowRight style={{ marginLeft: "6px" }} />
                </button>
            </div>

            {isDrawerOpen && (
                <div className="mobile-nav-overlay visible" onClick={() => setDrawerOpen(false)} />
            )}

            <div className={`drawer ${isDrawerOpen ? "open" : ""}`}>
                <div className="drawer-header">
                    <img src={logo} alt="ConRa" className="mobile-nav-icon-full drawer-logo" />
                    <button
                        className="drawer-close-round"
                        onClick={() => setDrawerOpen(false)}
                        aria-label="Close menu"
                    >
                        &times;
                    </button>
                </div>

                <button
                    className="drawer-get-started"
                    onClick={() => {
                        if (location.pathname === "/solutions/borderless") {
                            setDrawerOpen(false);
                            setTimeout(() => document.getElementById("register")?.scrollIntoView({ behavior: "smooth" }), 100);
                        } else {
                            window.open("https://web.conra.net", "_blank");
                        }
                    }}
                >
                    Get Started <FaArrowRight style={{ marginLeft: 8 }} />
                </button>

                <ul className="drawer-menu">
                    {/* Products submenu — only when both products are built in */}
                    {IS_DUAL_MODE && (
                    <li className="nav-link-black drawer-products">
                        <button
                            className={`drawer-products-trigger ${PRODUCTS.some((p) => isActive(p.path)) ? "is-selected" : ""}`}
                            onClick={() => setProductsOpen(!productsOpen)}
                        >
                            Products <FaChevronDown className={`nav-chevron ${productsOpen ? "open" : ""}`} />
                        </button>
                        {productsOpen && (
                            <ul className="drawer-submenu">
                                {PRODUCTS.map((p) => (
                                    <li
                                        key={p.path}
                                        className={`drawer-submenu-item ${isActive(p.path) ? "is-selected" : ""}`}
                                        onClick={() => { navigate(p.path); setDrawerOpen(false); setProductsOpen(false); }}
                                    >
                                        {p.label}
                                    </li>
                                ))}
                            </ul>
                        )}
                    </li>
                    )}

                    {/* Management flow: Features link */}
                    {isManagement && (
                        <li
                            className={`nav-link-black ${isActive(SECTION_PATHS.features) ? "is-selected" : ""}`}
                            onClick={() => handleNavigation("Features")}
                        >
                            Features
                        </li>
                    )}

                    {/* Published flow: Features submenu */}
                    {!isManagement && (
                        <li className="nav-link-black drawer-products">
                            <button
                                className={`drawer-products-trigger ${[...MENU_FEATURES, FEATURED_FEATURE].some((s) => s && isActive(`/solutions/${s.slug}`)) ? "is-selected" : ""}`}
                                onClick={() => { setFeaturesOpen(!featuresOpen); setSolutionsOpen(false); }}
                            >
                                Features <FaChevronDown className={`nav-chevron ${featuresOpen ? "open" : ""}`} />
                            </button>
                            {featuresOpen && (
                                <ul className="drawer-submenu">
                                    {[...MENU_FEATURES, ...(FEATURED_FEATURE ? [FEATURED_FEATURE] : [])].map((s) => (
                                        <li
                                            key={s.slug}
                                            className={`drawer-submenu-item ${isActive(`/solutions/${s.slug}`) ? "is-selected" : ""}`}
                                            onClick={() => {
                                                navigate(`/solutions/${s.slug}`);
                                                setDrawerOpen(false);
                                                setFeaturesOpen(false);
                                            }}
                                        >
                                            {s.menuLabel}
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </li>
                    )}

                    {/* Published flow: Solutions submenu (NGOs & Contractors) */}
                    {!isManagement && (
                        <li className="nav-link-black drawer-products">
                            <button
                                className={`drawer-products-trigger ${MENU_SOLUTIONS.some((s) => isActive(`/solutions/${s.slug}`)) ? "is-selected" : ""}`}
                                onClick={() => { setSolutionsOpen(!solutionsOpen); setFeaturesOpen(false); }}
                            >
                                Solutions <FaChevronDown className={`nav-chevron ${solutionsOpen ? "open" : ""}`} />
                            </button>
                            {solutionsOpen && (
                                <ul className="drawer-submenu">
                                    {MENU_SOLUTIONS.map((s) => (
                                        <li
                                            key={s.slug}
                                            className={`drawer-submenu-item ${isActive(`/solutions/${s.slug}`) ? "is-selected" : ""}`}
                                            onClick={() => {
                                                navigate(`/solutions/${s.slug}`);
                                                setDrawerOpen(false);
                                                setSolutionsOpen(false);
                                            }}
                                        >
                                            {s.menuLabel}
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </li>
                    )}

                    {["Pricing", "About", "Search"].map((section) => (
                        <li
                            className={`nav-link-black ${isActive(SECTION_PATHS[section.toLowerCase()]) ? "is-selected" : ""}`}
                            key={section}
                            onClick={() => handleNavigation(section)}
                        >
                            {section}
                        </li>
                    ))}
                </ul>
            </div>
        </nav>
    );
};

export default MobileNav;
